import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Loader2, CheckCircle, XCircle, Bell, Users, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';

import config from '../../config';
const API = `${config.BACKEND_URL}/api`;

export const AdminActions = () => {
  const [testEmail, setTestEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);

  const [clearDataDialogOpen, setClearDataDialogOpen] = useState(false);
  const [clearDataConfirmText, setClearDataConfirmText] = useState('');
  const [clearingData, setClearingData] = useState(false);
  const [clearDataResult, setClearDataResult] = useState(null);

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingEmail(true);
    setEmailResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/test-email`, { 
        to_email: testEmail 
      });
      setEmailResult({ success: true, message: response.data.message });
      toast.success('Test email sent successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send test email';
      setEmailResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendTestNotification = async () => {
    setSendingNotification(true);
    setNotificationResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/test-notification`);
      const hasConnections = response.data.connected_users > 0;
      setNotificationResult({ 
        success: hasConnections, 
        message: response.data.message,
        connectedUsers: response.data.connected_users
      });
      if (hasConnections) {
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send test notification';
      setNotificationResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSendingNotification(false);
    }
  };

  const handleClearData = async () => {
    setClearingData(true);
    try {
      const response = await axios.delete(`${API}/admin/clear-data`);
      setClearDataResult(response.data.deleted);
      toast.success('All data cleared');
      setClearDataDialogOpen(false);
      setClearDataConfirmText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearingData(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-actions-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Actions</h1>
        <p className="text-muted-foreground mt-1">
          Administrative actions and utilities
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Test Email Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Test Email
            </CardTitle>
            <CardDescription>
              Send a test email to verify SMTP configuration is working correctly
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Recipient Email</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="test@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="test-email-input"
              />
            </div>
            
            <Button 
              onClick={handleSendTestEmail} 
              disabled={sendingEmail || !testEmail.trim()}
              className="w-full"
              data-testid="send-test-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test Email
                </>
              )}
            </Button>

            {emailResult && (
              <div className={`flex items-center gap-2 p-3 rounded-md text-sm ${
                emailResult.success 
                  ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' 
                  : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
              }`}>
                {emailResult.success ? (
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 flex-shrink-0" />
                )}
                <span>{emailResult.message}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test Notification Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Test WebSocket Notification
            </CardTitle>
            <CardDescription>
              Send a test push notification to all currently logged-in users via WebSocket
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
              <p className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                This will broadcast a test message to all users who have an active WebSocket connection.
              </p>
            </div>
            
            <Button 
              onClick={handleSendTestNotification} 
              disabled={sendingNotification}
              className="w-full"
              data-testid="send-test-notification-btn"
            >
              {sendingNotification ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4 mr-2" />
                  Send Test Notification
                </>
              )}
            </Button>

            {notificationResult && (
              <div className={`flex items-center gap-2 p-3 rounded-md text-sm ${
                notificationResult.success 
                  ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' 
                  : 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
              }`}>
                {notificationResult.success ? (
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 flex-shrink-0" />
                )}
                <span>{notificationResult.message}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>Irreversible actions - proceed with caution</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 p-4 border border-destructive/30 rounded-md">
            <div>
              <p className="font-medium">Clear All Data</p>
              <p className="text-sm text-muted-foreground mt-1">
                Permanently deletes all projects, releases, user stories, comments, ideas,
                audit logs, and API tokens. Users and statuses are kept.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setClearDataDialogOpen(true)}
              className="gap-2 flex-shrink-0"
              data-testid="clear-data-btn"
            >
              <Trash2 className="h-4 w-4" />
              Clear All Data
            </Button>
          </div>

          {clearDataResult && (
            <div className="flex items-start gap-2 p-3 rounded-md text-sm bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
              <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p>All data cleared successfully.</p>
                <p className="mt-1 font-mono text-xs">
                  {Object.entries(clearDataResult).map(([key, count]) => `${key}: ${count}`).join(', ')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clear Data Confirmation Dialog */}
      <AlertDialog
        open={clearDataDialogOpen}
        onOpenChange={(open) => {
          setClearDataDialogOpen(open);
          if (!open) setClearDataConfirmText('');
        }}
      >
        <AlertDialogContent data-testid="clear-data-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Clear all data?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  This permanently deletes <strong>all projects, releases, user stories,
                  comments, ideas, audit logs, and API tokens</strong>. Users and statuses
                  are kept. This cannot be undone.
                </p>
                <div className="space-y-2 pt-1">
                  <Label htmlFor="clear-data-confirm">
                    Type <span className="font-mono font-semibold">DELETE</span> to confirm
                  </Label>
                  <Input
                    id="clear-data-confirm"
                    value={clearDataConfirmText}
                    onChange={(e) => setClearDataConfirmText(e.target.value)}
                    autoComplete="off"
                    data-testid="clear-data-confirm-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleClearData}
              disabled={clearDataConfirmText !== 'DELETE' || clearingData}
              className="gap-2"
              data-testid="clear-data-confirm-btn"
            >
              {clearingData ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Clear All Data
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
